'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';

interface FormState {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const subjectOptions = [
  'General question',
  'Bug report',
  'Privacy concern',
  'Account issue',
  'Feature request',
  'Partnership',
  'Other',
];

export default function ContactPage() {
  const [form, setForm] = useState<FormState>({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;

    setStatus('loading');
    setErrorMsg('');

    try {
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
      const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

      const res = await fetch(`${supabaseUrl}/functions/v1/send-contact-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data?.error || 'Something went wrong');
      }

      setStatus('success');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err: unknown) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Failed to send message. Please try again.');
    }
  };

  const inputClass =
    'w-full rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none transition-all duration-150 focus:ring-2';
  const inputStyle = {
    background: 'var(--input)',
    border: '1px solid var(--glass-border)',
    '--tw-ring-color': 'var(--primary)',
  } as React.CSSProperties;

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)' }}>
      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/3 left-1/4 w-96 h-96 rounded-full opacity-10 blur-3xl" style={{ background: 'var(--primary)' }} />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full opacity-8 blur-3xl" style={{ background: 'var(--accent)' }} />
      </div>

      {/* Topbar */}
      <header className="glass-strong border-b border-[var(--glass-border)] sticky top-0 z-50">
        <div className="max-w-screen-lg mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/landing-page" className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-bold text-lg gradient-text">Uyir</span>
          </Link>
          <Link href="/landing-page" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            ← Back to Home
          </Link>
        </div>
      </header>

      <main className="relative max-w-screen-lg mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center space-y-3 pb-10">
          <h1 className="text-3xl md:text-4xl font-bold gradient-text">Get in Touch</h1>
          <p className="text-muted-foreground max-w-xl mx-auto text-sm md:text-base">
            We're real humans. Every message gets read. We typically respond within 24–48 hours.
          </p>
        </div>

        <div className="flex justify-center">
          {/* Contact form */}
          <div className="w-full max-w-2xl">
            {status === 'success' ? (
              <div className="glass rounded-2xl p-10 text-center space-y-4 fade-in">
                <div className="text-5xl">💜</div>
                <h2 className="text-xl font-bold text-foreground">Message sent!</h2>
                <p className="text-muted-foreground text-sm">
                  Thanks for reaching out. We'll get back to you within 24–48 hours.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-150 hover:opacity-90"
                  style={{ background: 'rgba(124, 111, 247, 0.15)', color: 'var(--primary)' }}
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="glass rounded-2xl p-6 md:p-8 space-y-5" aria-label="Contact form" noValidate>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-1.5">
                    <label htmlFor="contact-name" className="text-xs font-medium text-muted-foreground">Your name *</label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Arjun"
                      required
                      autoComplete="name"
                      aria-required="true"
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="contact-email" className="text-xs font-medium text-muted-foreground">Email address *</label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="arjun@example.com"
                      required
                      autoComplete="email"
                      aria-required="true"
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="contact-subject" className="text-xs font-medium text-muted-foreground">Subject</label>
                  <select
                    id="contact-subject"
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    className={inputClass}
                    style={inputStyle}
                  >
                    <option value="">Select a topic…</option>
                    {subjectOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="contact-message" className="text-xs font-medium text-muted-foreground">Message *</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="What's on your mind?"
                    required
                    rows={5}
                    aria-required="true"
                    className={`${inputClass} resize-none`}
                    style={inputStyle}
                  />
                </div>

                {status === 'error' && (
                  <p role="alert" className="text-sm rounded-xl px-4 py-3" style={{ background: 'rgba(236, 72, 153, 0.1)', color: '#f472b6' }}>
                    {errorMsg}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  aria-busy={status === 'loading'}
                  className="w-full py-3.5 rounded-xl gradient-bg text-white font-semibold text-sm hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2"
                >
                  {status === 'loading' ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Sending…
                    </span>
                  ) : (
                    'Send Message'
                  )}
                </button>

                <p className="text-xs text-muted-foreground text-center">
                  By submitting, you agree to our{' '}
                  <Link href="/privacy-policy" className="underline focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-1 rounded" style={{ color: 'var(--primary)' }}>Privacy Policy</Link>.
                </p>
              </form>
            )}
          </div>
        </div>

        {/* Footer nav */}
        <div className="flex flex-wrap justify-center gap-6 pt-10 text-sm text-muted-foreground">
          <Link href="/privacy-policy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
          <Link href="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link>
          <Link href="/help-center" className="hover:text-foreground transition-colors">Help Center</Link>
        </div>
      </main>
    </div>
  );
}
